import type { SelectEventArgs } from '@syncfusion/ej2-react-schedule'
import { useNotification } from 'app/providers'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import { UtilService } from 'common/services/utilService'
import { add, compareAsc, differenceInMinutes, isAfter } from 'date-fns'
import dayjs from 'dayjs'
import { COUNT_AS_OVERLAPPING, ROOM_STATUS } from 'features/Home/constants/infoConstants'
import type { IPatientRoom } from 'features/Home/interfaces/IInfoPatient'
import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'
import { InfoService } from 'features/Home/services/infoService'
import type { DoctorAvailabilityGroup } from 'features/Schedule/api/schedule.types'
import { useCallback, useMemo, useState } from 'react'
import {
  buildScheduleUpdatePayload,
  formatDroppedScheduleTime,
  isWithinAvailableSlots,
  resolveScheduleRoomId,
  snapToFiveMinutes,
} from './helpers'
import { mapSchedulesToEvents } from './mapSchedulesToEvents'
import { revertScheduleDomChange } from './safeRefreshScheduleEvents'
import { isSelectionRangeUnavailable } from './scheduleAvailability'
import { usePendingAwayCreation } from './usePendingAwayCreation'

type PreparedScheduleChange = {
  adjustedData: any
}

type UseScheduleCalendarResponse = {
  pendingCreation: { start: Date; end: Date; resourceId: string } | null
  pendingAwayMove: IPatientSchedule | null
  cancelCreation: () => void
  confirmCreation: () => void
  destinationIsUnavailable: (data: IPatientSchedule) => boolean
  queueAwayMove: (data: IPatientSchedule) => void
  cancelAwayMove: () => void
  confirmAwayMove: () => void
  awaitConfirmationEvent: any
  setAwaitConfirmationEvent: (event: any) => void
  prepareScheduleChange: (data: any) => PreparedScheduleChange | null
  commitScheduleChange: (prepared: PreparedScheduleChange, event: any) => Promise<void>
  handleScheduleChange: (data: any) => Promise<void>
  proceedToUpdateEvents: () => Promise<void>
  events: IPatientSchedule[]
  handleSelect: (props: any) => void
}

type UseScheduleCalendarProps = {
  resources?: { resourceId: string; roomResourceId?: string; staffId?: number }[]
  dayInterval: { min: string; max: string }
  disabledEverything?: boolean
  disabledIntervals: any[]
  formattedDate: string
  rooms: IPatientRoom[]
  schedules: IPatientSchedule[]
  timeZone: string
  scheduleObj: any
  prepopulateTimeOnClick: number
  handleUpdateEvents: (adjustedData: any, event: any) => Promise<boolean>
  createCustomAppointment?: (
    start: Date,
    end: Date,
    resourceId: string,
    awayConfirmed?: boolean,
  ) => void
  instanceKey?: string
  resourceGrouping?: 'rooms' | 'staff'
  availability?: DoctorAvailabilityGroup[]
  availabilityStaffIds?: number[]
  emrLinkedUserIds?: Set<number>
}

export const useScheduleCalendar = ({
  dayInterval,
  resources = [],
  disabledEverything = false,
  disabledIntervals,
  formattedDate,
  rooms,
  schedules,
  timeZone,
  scheduleObj,
  prepopulateTimeOnClick,
  createCustomAppointment,
  handleUpdateEvents,
  instanceKey,
  resourceGrouping = 'rooms',
  availability = [],
  availabilityStaffIds = [],
  emrLinkedUserIds,
}: UseScheduleCalendarProps): UseScheduleCalendarResponse => {
  const [awaitConfirmationEvent, setAwaitConfirmationEvent] = useState<any>(null)

  const availabilityUserIds = useCallback(
    (ids: number[]) => (emrLinkedUserIds ? ids.filter((id) => emrLinkedUserIds.has(id)) : ids),
    [emrLinkedUserIds],
  )

  const { pendingCreation, requestCreation, cancelCreation, confirmCreation } =
    usePendingAwayCreation(createCustomAppointment)
  const [pendingAwayMove, setPendingAwayMove] = useState<IPatientSchedule | null>(null)

  const { setNotification } = useNotification()

  const isBetween = (number: number, lowerBound: number, upperBound: number): boolean => {
    return number >= lowerBound && number <= upperBound
  }

  const shouldProceedToUpdate = ({
    resourceId,
    start,
    end,
    event,
    room,
    isResizing,
  }: {
    resourceId: string
    start: any
    end: any
    event: any
    room: IPatientRoom
    isResizing?: boolean
    isDrag?: boolean
  }) => {
    if (room?.slots?.length) {
      const isAvailable = isWithinAvailableSlots({
        dateKey: formattedDate,
        slots: room.slots,
        start,
        end,
      })

      if (!isAvailable) {
        setNotification({
          title: 'Update schedule error',
          description: 'Appointment must be within available time slots.',
          type: ALERT_CONSTANTS.ERROR,
        })
        return false
      }
    }

    const eventsFromRoom = schedules.filter(
      (item) =>
        item.room?.code === resourceId &&
        item.id !== event.id &&
        COUNT_AS_OVERLAPPING.includes(item.status?.code),
    )

    const { nrOfOverlappingEvents, overlappingEvents } =
      InfoService.getNumberOfOverlappingIntervals({ start, end }, eventsFromRoom, resourceId)

    if (!isBetween(nrOfOverlappingEvents, 1, room.max_patients || 1) && !room?.is_waiting_room) {
      setNotification({
        title: 'Update schedule error',
        description: `You reached the max number of ${
          room.max_patients || 1
        } patients at the same time for this room.`,
        type: ALERT_CONSTANTS.ERROR,
      })
      return false
    }

    const shouldApplyRoomRule = !InfoService.checkIfCurrentDateOverlapDisabledIntervals(
      disabledIntervals,
      room,
      dayInterval,
      timeZone,
    )
    if (
      shouldApplyRoomRule &&
      room &&
      room.status &&
      room.status.code !== ROOM_STATUS.FREE &&
      room.status.code !== ROOM_STATUS.CLEANUP &&
      room.status.code !== ROOM_STATUS.BUSY &&
      !isResizing
    ) {
      return false
    }

    const { start_time: event_start, end_time: event_end, room_id: eventRoom } = event
    const { overlappingEvents: overlappingEventsBeforeUpdate } =
      InfoService.getNumberOfOverlappingIntervals(
        { start: event_start, end: event_end },
        eventsFromRoom,
        eventRoom,
      )

    const shouldShowOverlappingEventsNotification =
      nrOfOverlappingEvents > 1 &&
      !UtilService.arrDeepEqual(overlappingEventsBeforeUpdate, overlappingEvents) &&
      room.value !== rooms[rooms.length - 1].value

    if (shouldShowOverlappingEventsNotification) {
      setAwaitConfirmationEvent({
        start,
        end,
        event,
        room,
      })

      return false
    }

    return true
  }

  const revertScheduleChange = useCallback(() => {
    revertScheduleDomChange(scheduleObj?.current)
  }, [scheduleObj])

  const staffIdsForResource = useCallback(
    (resourceId: string) => {
      const resource = resources.find((item) => item.resourceId === resourceId)
      const ids =
        resourceGrouping === 'staff'
          ? [Number(resourceId)].filter((id) => Number.isFinite(id) && id > 0)
          : resource?.staffId != null
            ? [resource.staffId].filter(Boolean)
            : availabilityStaffIds
      return availabilityUserIds(ids)
    },
    [availabilityStaffIds, availabilityUserIds, resourceGrouping, resources],
  )

  const destinationIsUnavailable = useCallback(
    (data: IPatientSchedule) => {
      const start = snapToFiveMinutes(new Date(data.start_time))
      let end = snapToFiveMinutes(new Date(data.end_time))
      if (differenceInMinutes(end, start) < 5) end = dayjs(end).add(5, 'minutes').toDate()
      const staffIds = staffIdsForResource(resolveScheduleRoomId(data.room_id))
      // Drag data is a display copy, already site digits; a zone here would shift it.
      return isSelectionRangeUnavailable(availability, staffIds, start, end)
    },
    [availability, staffIdsForResource],
  )

  const queueAwayMove = (data: IPatientSchedule) => setPendingAwayMove(data)
  const cancelAwayMove = () => {
    setPendingAwayMove(null)
    revertScheduleChange()
  }

  const prepareScheduleChange = (data: IPatientSchedule): PreparedScheduleChange | null => {
    // No in-flight guard here: overlapping saves are safe because structural
    // cache merges are version-guarded (see upsertPatientInDraft), so blocking
    // rapid edits would only silently revert the user's drop.
    if (disabledEverything) {
      return null
    }

    const start = snapToFiveMinutes(new Date(data.start_time))
    let end = snapToFiveMinutes(new Date(data.end_time))

    const originalEvent = schedules.find((event) => event.id === data.id)
    const isResizing = originalEvent
      ? originalEvent.start_time === data.start_time || originalEvent.end_time === data.end_time
      : false

    const durationInMinutes = differenceInMinutes(end, start)
    if (durationInMinutes < 5) {
      end = dayjs(end).add(5, 'minutes').toDate()
    }

    if (resourceGrouping === 'staff') {
      // Lunch and away time only warn (tooltip and shading); the drop always goes through.
      const staffId = Number(resolveScheduleRoomId(data.room_id))
      const currentRoom = originalEvent?.room ?? data.room
      const roomCode = currentRoom?.value ?? currentRoom?.code

      const adjustedData = buildScheduleUpdatePayload(data, {
        room: roomCode,
        start_time: formatDroppedScheduleTime(start),
        end_time: formatDroppedScheduleTime(end),
        consultation_staff: Number.isFinite(staffId) ? staffId : data.consultation_staff,
      })

      return { adjustedData }
    }

    const resourceId = resolveScheduleRoomId(data.room_id)
    const resource = resources.find((item) => item.resourceId === resourceId)
    const roomId = resource?.roomResourceId ?? resourceId
    const destinationData =
      resource?.staffId != null ? { ...data, consultation_staff: resource.staffId } : data
    const [label, value] = roomId.split('/')
    const room =
      rooms.find((item: IPatientRoom) => item.label === label && item.value === value) ??
      rooms.find(
        (item: IPatientRoom) =>
          item.value === value || item.value === roomId || item.code === roomId,
      )
    const destinationRoomCode = room?.code ?? room?.value ?? value

    const shouldUpdateEvent = shouldProceedToUpdate({
      resourceId: roomId,
      start,
      end,
      event: { ...destinationData, room_id: roomId },
      room,
      isResizing,
    })

    if (!shouldUpdateEvent) {
      return null
    }

    const { nrOfOverlappingEvents } = InfoService.getNumberOfOverlappingIntervals(
      { start, end },
      schedules,
      destinationRoomCode,
    )

    if (nrOfOverlappingEvents > 1) {
      setAwaitConfirmationEvent({
        start,
        end,
        event: { ...destinationData, room_id: roomId },
        room,
      })
      return null
    }

    const adjustedData = buildScheduleUpdatePayload(destinationData, {
      room: room?.value ?? value,
      start_time: formatDroppedScheduleTime(start),
      end_time: formatDroppedScheduleTime(end),
    })

    return { adjustedData }
  }

  const commitScheduleChange = async (
    { adjustedData }: PreparedScheduleChange,
    event: IPatientSchedule,
  ): Promise<void> => {
    try {
      await handleUpdateEvents(adjustedData, event)
    } catch {
      revertScheduleChange()
    }
  }

  const handleScheduleChange = async (data: IPatientSchedule): Promise<void> => {
    const prepared = prepareScheduleChange(data)

    if (!prepared) {
      revertScheduleChange()
      return
    }

    await commitScheduleChange(prepared, data)
  }

  const confirmAwayMove = () => {
    const data = pendingAwayMove
    setPendingAwayMove(null)
    if (data) handleScheduleChange(data)
  }

  const proceedToUpdateEvents = useCallback(async () => {
    if (!awaitConfirmationEvent) {
      return
    }

    const { start, end, event, room } = awaitConfirmationEvent
    const adjustedData = buildScheduleUpdatePayload(event, {
      room: room?.value,
      start_time: formatDroppedScheduleTime(start),
      end_time: formatDroppedScheduleTime(end),
    })

    const successUpdate = await handleUpdateEvents(adjustedData, event)
    if (!successUpdate) {
      return
    }

    scheduleObj?.current?.saveEvent({ ...event })
    setAwaitConfirmationEvent(null)
  }, [awaitConfirmationEvent, handleUpdateEvents])

  const eventsToShow = useMemo(
    () =>
      mapSchedulesToEvents(
        schedules,
        timeZone,
        (event) =>
          resourceGrouping === 'staff'
            ? String(event.consultation_staff || 0)
            : resources.find(
                (resource) =>
                  resource.roomResourceId === `${event.room?.label}/${event.room?.value}` &&
                  resource.staffId === Number(event.consultation_staff || 0),
              )?.resourceId ?? `${event.room?.label}/${event.room?.value}`,
        instanceKey,
      ),
    [schedules, timeZone, resourceGrouping, instanceKey, resources],
  )

  const handleSelect = useCallback(
    (props: SelectEventArgs) => {
      const cellData = props.data as
        | { room_id: string; start_time: Date; end_time: Date }
        | undefined
      // Clicking an appointment also fires select with the event's own times; it's not a new slot.
      if (disabledEverything || !cellData || props.requestType === 'eventSelect') {
        return
      }

      if ((props.event as Event)?.type === 'mouseleave' && !cellData.start_time) {
        return
      }

      const resource = resources.find((item) => item.resourceId === cellData.room_id)
      const { start_time, room_id } = cellData
      let { end_time } = cellData

      const diffInMinutes = differenceInMinutes(end_time, start_time)
      const prepPopulateTime = prepopulateTimeOnClick
      const updatedEnd = add(start_time, { minutes: prepPopulateTime })
      const disabledIntervalsForRoom = disabledIntervals
        .filter((interval: any) => interval.resourceId === (resource?.roomResourceId ?? room_id))
        .sort((a: any, b: any) => compareAsc(a.start, b.start))

      const firstDisabledInterval = disabledIntervalsForRoom.find(
        (interval: any) =>
          isAfter(interval.start, start_time) && isAfter(updatedEnd, interval.start),
      )

      const endDayDate = new Date(`${formattedDate} ${dayInterval.max}`)

      const dateToCheck = firstDisabledInterval?.start || endDayDate

      if (diffInMinutes < prepPopulateTime && !isAfter(updatedEnd, dateToCheck)) {
        end_time = updatedEnd
      } else if (diffInMinutes < prepPopulateTime && isAfter(updatedEnd, dateToCheck)) {
        end_time = dateToCheck
      }

      const staffIds = availabilityUserIds(
        resourceGrouping === 'staff'
          ? [Number(room_id)].filter(Boolean)
          : resource?.staffId != null
          ? [resource.staffId].filter(Boolean)
          : availabilityStaffIds,
      )
      // Cell times are display copies, already site digits; a zone here would shift them.
      requestCreation(
        { start: start_time, end: end_time, resourceId: room_id },
        isSelectionRangeUnavailable(availability, staffIds, start_time, end_time),
      )
    },
    [
      disabledEverything,
      resources,
      timeZone,
      dayInterval.max,
      prepopulateTimeOnClick,
      rooms,
      disabledIntervals,
      formattedDate,
      requestCreation,
      resourceGrouping,
      availability,
      availabilityStaffIds,
      availabilityUserIds,
    ],
  )

  return {
    pendingCreation,
    pendingAwayMove,
    cancelCreation,
    confirmCreation,
    destinationIsUnavailable,
    queueAwayMove,
    cancelAwayMove,
    confirmAwayMove,
    prepareScheduleChange,
    commitScheduleChange,
    handleScheduleChange,
    events: eventsToShow,
    awaitConfirmationEvent,
    setAwaitConfirmationEvent,
    proceedToUpdateEvents,
    handleSelect,
  }
}
